import React from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { Map, List } from 'immutable';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Table, TableContainer, TableHead, TableRow, TableCell, TableBody, TextField, Divider, Paper } from '@mui/material';
import Button from 'src/component/UI/Button';
import variables from 'src/style/variable/variables.module.scss';
import { useTranslation } from 'src/i18n';

// Specialized table component for commissioning circuit extras (e.g., junction and control panel data).
// Provides dynamic row management (add/delete) with responsive layout for mobile and desktop views.
// Differs from DataTable in that it's form-oriented with cell-level onChange handlers rather than
// displaying static report data.
export default function CircuitDynamicTable (props) {
  const { title, columns, data, extrasKey, onChange, readOnly } = props;
  const { t } = useTranslation();
  const isMobile = useMediaQuery(`(max-width: ${variables.phoneWidth})`);

  const emptyRow = () => {
    const row = {};
    columns.forEach(col => { row[col.key] = ''; });
    return Map(row);
  };

  const addRow = () => {
    onChange(['extras', extrasKey], (data || List()).push(emptyRow()));
  };

  const onCellChange = (rowIndex, colKey, val) => {
    onChange(['extras', extrasKey], data.setIn([rowIndex, colKey], val));
  };

  const desktopTable = () => (
    <TableContainer>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            {columns.map(col => (
              <TableCell key={col.key} className='main-headers'>{col.label}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {(data || List()).map((rowData, rowIndex) => (
            <TableRow key={rowIndex}>
              {columns.map(col => (
                <TableCell key={col.key} className='body-cell'>
                  <TextField
                    fullWidth
                    disabled={readOnly}
                    value={rowData.get(col.key) || ''}
                    onChange={(e) => onCellChange(rowIndex, col.key, e.target.value)}
                  />
                </TableCell>
              ))}
            </TableRow>
          )).toArray()}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const mobileCards = () => (
    <>
      {(data || List()).map((rowData, rowIndex) => (
        <React.Fragment key={rowIndex}>
          <TableContainer className='mobile-junction-table'>
            <TableBody>
              {columns.map((col, idx) => (
                <TableRow key={`${col.key}-${idx}`}>
                  <TableCell className='sub-headers min-sub-headers center-items small-border remove-padding-for-junction-table2'>
                    <div>{col.label}</div>
                  </TableCell>
                  <TableCell className='body-cell'>
                    <TextField
                      fullWidth
                      disabled={readOnly}
                      value={rowData.get(col.key) || ''}
                      onChange={(e) => onCellChange(rowIndex, col.key, e.target.value)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </TableContainer>
          <Divider style={{ marginTop: '20px', marginBottom: '20px' }} />
        </React.Fragment>
      )).toArray()}
    </>
  );

  return (
    <Paper sx={{ marginBottom: '30px', padding: '16px' }}>
      <h3>{title}</h3>
      {isMobile ? mobileCards() : desktopTable()}
      <div className='button-bar at-Junction'>
        <Button onClick={addRow} disabled={readOnly} border background backgroundColor={'white'} className='add-row'>
          {t('commissioningCircuit.common.addRow')}
        </Button>
      </div>
    </Paper>
  );
}

CircuitDynamicTable.propTypes = {
  title: PropTypes.string.isRequired,
  columns: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  })).isRequired,
  data: ImmutablePropTypes.list,
  extrasKey: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
};

CircuitDynamicTable.defaultProps = {
  data: List(),
  readOnly: false,
};
